/**
 * Alshallal Pastry Company HR System - Requests Admin Page
 * Phase 5: Generalized Requests & Approvals
 * Phase 18.2.3.1: HR Override capability for pending_manager requests
 * HR Admin view for managing all requests and request types
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RequestFilters, nationalityLabel } from '@/components/requests/RequestFilters';
import { RequestStatistics } from '@/components/requests/RequestStatistics';
import { DeleteRequestButton } from '@/components/requests/DeleteRequestButton';
import { RequestHistory } from '@/components/requests/RequestHistory';
import { BackfillReview } from '@/components/requests/BackfillReview';
import { format } from 'date-fns';
import {
  ClipboardList,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  User,
  Building,
  Paperclip,
  Settings,
  Eye,
  FileText,
  Download,
  Loader2
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useListViewState } from '../../hooks/useListViewState';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  classifyManagerAction,
  classifyHrAction,
} from '@/lib/leaveDecisionClassifier';
import { DecisionBadge } from '@/components/shared/DecisionBadge';
import DocumentPreview from '@/components/documents/DocumentPreview';
import { downloadWithAuth } from '@/lib/downloadWithAuth';
import { usePermission, Permission } from '@/contexts/PermissionContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  pending_manager: { bg: 'bg-amber-100 text-amber-800', icon: Clock, label: { en: 'Pending Manager', ar: 'بانتظار المدير' } },
  pending_hr: { bg: 'bg-blue-100 text-blue-800', icon: Clock, label: { en: 'Pending HR', ar: 'بانتظار الموارد البشرية' } },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, label: { en: 'Approved', ar: 'موافق عليه' } },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle, label: { en: 'Rejected', ar: 'مرفوض' } },
};

const STATUS_OPTIONS = [
  { value: 'all', label: { en: 'All Statuses', ar: 'جميع الحالات' } },
  { value: 'pending_manager', label: { en: 'Pending Manager', ar: 'بانتظار المدير' } },
  { value: 'pending_hr', label: { en: 'Pending HR', ar: 'بانتظار الموارد البشرية' } },
  { value: 'approved', label: { en: 'Approved', ar: 'موافق عليه' } },
  { value: 'rejected', label: { en: 'Rejected', ar: 'مرفوض' } },
];

const RequestsAdminPage = () => {
  const { token } = useAuth();
  const { isRTL, language } = useLanguage();

  // State
  const [requests, setRequests] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters and pagination live in the URL, so leaving this screen and coming
  // back returns to the same page with the same filters.
  const [view, setView] = useListViewState('requests-admin', { page: 1, status: 'all', type: 'all', nationality: '', branch_id: '', event_from: '', event_to: '' });
  const { page, status: statusFilter, type: typeFilter } = view;
  const setPage = useCallback((next) => setView({ page: typeof next === 'function' ? next(page) : next }), [setView, page]);
  const setStatusFilter = useCallback((value) => setView({ status: value, page: 1 }), [setView]);
  const setTypeFilter = useCallback((value) => setView({ type: value, page: 1 }), [setView]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Dialogs
  const [typesDialog, setTypesDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, request: null });
  const [previewDialog, setPreviewDialog] = useState({ open: false, attachment: null });
  
  // Phase 18.2.3.1: HR Override dialog
  const [overrideDialog, setOverrideDialog] = useState({ open: false, request: null, action: null });
  const [overrideReason, setOverrideReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { hasPermission } = usePermission();
  const canBypass = hasPermission(Permission.HR_BYPASS);

  // Fetch request types
  const fetchRequestTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/requests/types?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequestTypes(response.data.request_types || []);
    } catch (err) {
      console.error('Failed to fetch request types:', err);
    }
  }, [token]);

  // Fetch all requests
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '15'
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('request_type_id', typeFilter);
      ['nationality', 'branch_id', 'event_from', 'event_to'].forEach(key => { if (view[key]) params.append(key, view[key]); });

      const response = await axios.get(`${API_URL}/requests/admin/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRequests(response.data.requests || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter, typeFilter, view.nationality, view.branch_id, view.event_from, view.event_to]);

  useEffect(() => {
    fetchRequestTypes();
  }, [fetchRequestTypes]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  const getStatusStyle = (status) => status === 'cancelled' ? { bg: 'bg-slate-100 text-slate-700', icon: XCircle, label: { en: 'Cancelled', ar: 'ملغي' } } : STATUS_STYLES[status] || { bg: 'bg-slate-100', icon: Clock, label: { en: 'Unknown', ar: 'غير معروف' } };
  const getStatusLabel = (status) => {
    const style = getStatusStyle(status);
    return language === 'ar' ? style.label.ar : style.label.en;
  };

  // Phase 18.2.3.1: HR Override action
  const handleOverride = async () => {
    if (!overrideDialog.request || overrideReason.length < 10) return;
    
    try {
      setSubmitting(true);
      
      await axios.post(`${API_URL}/requests/${overrideDialog.request.id}/hr-override`, {
        action: overrideDialog.action,
        reason: overrideReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOverrideDialog({ open: false, request: null, action: null });
      setOverrideReason('');
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Override action failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Stats
  const stats = {
    total: total,
    pending_manager: requests.filter(r => r.status === 'pending_manager').length,
    pending_hr: requests.filter(r => r.status === 'pending_hr').length,
  };

  return (
    <div className="space-y-6" data-testid="requests-admin-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'إدارة الطلبات' : 'Requests Admin'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isRTL ? 'عرض وإدارة جميع الطلبات' : 'View and manage all requests'}
          </p>
        </div>
        <Button variant="outline" onClick={() => setTypesDialog(true)} data-testid="request-types-btn">
          <Settings className="h-4 w-4 me-2" />
          {isRTL ? 'أنواع الطلبات' : 'Request Types'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2"><RequestHistory onChange={fetchRequests} /><BackfillReview onApplied={fetchRequests} /></div>
      <RequestFilters values={view} refreshKey={total} onChange={value => setView({ ...value, page: 1 })} />
      <RequestStatistics filters={{ nationality: view.nationality, branch_id: view.branch_id, event_from: view.event_from, event_to: view.event_to, status: statusFilter, request_type_id: typeFilter }} refreshKey={requests} />
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {language === 'ar' ? opt.label.ar : opt.label.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="type-filter">
                  <SelectValue placeholder={isRTL ? 'نوع الطلب' : 'Request Type'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الأنواع' : 'All Types'}</SelectItem>
                  {requestTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchRequests} disabled={loading}>
              <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
              {isRTL ? 'تحديث' : 'Refresh'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>{isRTL ? 'جميع الطلبات' : 'All Requests'}</CardTitle>
          <CardDescription>
            {isRTL ? `${total} طلب` : `${total} requests`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="py-12 text-center">
              <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {isRTL ? 'لا توجد طلبات' : 'No Requests'}
              </h3>
              <p className="text-slate-600">
                {isRTL ? 'لا توجد طلبات تطابق الفلاتر المحددة' : 'No requests match the selected filters'}
              </p>
            </div>
          ) : (
            <>
              <TooltipProvider delayDuration={150}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead>{isRTL ? 'نوع الطلب' : 'Request Type'}</TableHead>
                    <TableHead>{isRTL ? 'تاريخ التقديم' : 'Submitted'}</TableHead>
                    <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead>{isRTL ? 'المدير' : 'Manager'}</TableHead>
                    <TableHead>{isRTL ? 'الموارد البشرية' : 'HR'}</TableHead>
                    <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => {
                    const statusStyle = getStatusStyle(request.status);
                    const StatusIcon = statusStyle.icon;
                    // Only a bypass holder may skip the manager's stage, so
                    // nobody else is shown a button that will refuse them.
                    const canOverride = request.status === 'pending_manager' && canBypass;
                    
                    return (
                      <TableRow key={request.id} data-testid={`request-row-${request.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-400" />
                            <div>
                              <div className="font-medium flex items-center gap-2">
                                {request.employee_name?.trim() || 'Unknown'}
                                {request.attachments?.length > 0 && (
                                  <Badge
                                    variant="outline"
                                    className="gap-1 border-blue-200 bg-blue-50 text-blue-700 font-normal"
                                    title={isRTL ? 'يوجد مستند مرفق' : 'A supporting document is attached'}
                                    data-testid={`request-attachment-flag-${request.id}`}
                                  >
                                    <Paperclip className="h-3 w-3" />
                                    {request.attachments.length}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-slate-500">{request.employee_department || '-'}</p>
                              <p className="text-xs text-slate-500 break-words" data-testid={`request-snapshot-${request.id}`}>{nationalityLabel(request.filter_snapshot?.nationality)} · {request.filter_snapshot?.branch_name || 'Branch not recorded'}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {language === 'ar' && request.request_type_name_ar ? request.request_type_name_ar : request.request_type_name}
                          <p className="text-xs text-slate-500" data-testid={`request-event-date-${request.id}`}>{request.filter_snapshot?.event_date || 'Date not recorded'}</p>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {formatDate(request.submitted_at)}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusStyle.bg}>
                            <StatusIcon className="h-3 w-3 me-1" />
                            {getStatusLabel(request.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DecisionBadge
                            decision={classifyManagerAction(request)}
                            isRTL={isRTL}
                            testid={`req-manager-decision-${request.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <DecisionBadge
                            decision={classifyHrAction(request)}
                            isRTL={isRTL}
                            testid={`req-hr-decision-${request.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-end">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setViewDialog({ open: true, request })}
                              data-testid={`view-btn-${request.id}`}
                            >
                              <Eye className="h-4 w-4 me-1" />
                              {isRTL ? 'عرض' : 'View'}
                            </Button>
                            {/* Phase 18.2.3.1: HR Override buttons */}
                            <DeleteRequestButton row={request} onDeleted={fetchRequests} />
                            {canOverride && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-orange-600 hover:bg-orange-50"
                                  onClick={() => setOverrideDialog({ open: true, request, action: 'approve' })}
                                  title={isRTL ? 'تجاوز وموافقة' : 'Override Approve'}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-orange-600 hover:bg-orange-50"
                                  onClick={() => setOverrideDialog({ open: true, request, action: 'reject' })}
                                  title={isRTL ? 'تجاوز ورفض' : 'Override Reject'}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TooltipProvider>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-600">
                    {isRTL ? `عرض ${requests.length} من ${total}` : `Showing ${requests.length} of ${total}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Request Types Dialog */}
      <Dialog open={typesDialog} onOpenChange={setTypesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'أنواع الطلبات' : 'Request Types'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'أنواع الطلبات المتاحة في النظام' : 'Available request types in the system'}
            </DialogDescription>
          </DialogHeader>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isRTL ? 'الاسم' : 'Name'}</TableHead>
                <TableHead>{isRTL ? 'الوصف' : 'Description'}</TableHead>
                <TableHead>{isRTL ? 'مرفق مطلوب' : 'Requires Attachment'}</TableHead>
                <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requestTypes.map(type => (
                <TableRow key={type.id}>
                  <TableCell className="font-medium">
                    {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {language === 'ar' && type.description_ar ? type.description_ar : type.description}
                  </TableCell>
                  <TableCell>
                    {type.requires_attachment ? (
                      <Badge variant="outline" className="text-xs">
                        <Paperclip className="h-3 w-3 me-1" />
                        {isRTL ? 'نعم' : 'Yes'}
                      </Badge>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={type.is_active ? 'default' : 'secondary'}>
                      {type.is_active ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTypesDialog(false)}>
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, request: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {viewDialog.request && (language === 'ar' && viewDialog.request.request_type_name_ar ? viewDialog.request.request_type_name_ar : viewDialog.request?.request_type_name)}
            </DialogTitle>
            <DialogDescription>
              {viewDialog.request?.employee_name?.trim()} - {viewDialog.request?.employee_department}
            </DialogDescription>
          </DialogHeader>

          {viewDialog.request && (
            <div className="space-y-4 py-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{isRTL ? 'الحالة:' : 'Status:'}</span>
                <Badge className={getStatusStyle(viewDialog.request.status).bg}>
                  {getStatusLabel(viewDialog.request.status)}
                </Badge>
              </div>

              {/* Submitted */}
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{isRTL ? 'تاريخ التقديم:' : 'Submitted:'}</span>
                <span>{formatDate(viewDialog.request.submitted_at)}</span>
              </div>

              {/* Form Data */}
              {viewDialog.request.form_data && Object.keys(viewDialog.request.form_data).length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">{isRTL ? 'التفاصيل:' : 'Details:'}</h4>
                  <div className="bg-slate-50 p-3 rounded space-y-1">
                    {Object.entries(viewDialog.request.form_data).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {viewDialog.request.notes && (
                <div className="space-y-2">
                  <h4 className="font-medium">{isRTL ? 'ملاحظات:' : 'Notes:'}</h4>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{viewDialog.request.notes}</p>
                </div>
              )}

              {/* Manager Action — truthful per classifier (never defaults to Rejected) */}
              {(() => {
                const mgrDec = classifyManagerAction(viewDialog.request);
                if (!mgrDec || mgrDec.kind === 'none') return null;
                const bg =
                  mgrDec.tone === 'green' ? 'bg-green-50 text-green-700'
                  : mgrDec.tone === 'red' ? 'bg-red-50 text-red-700'
                  : mgrDec.tone === 'amber' ? 'bg-amber-50 text-amber-700'
                  : 'bg-slate-50 text-slate-700';
                const label = isRTL ? mgrDec.label_ar : mgrDec.label;
                const sub = isRTL ? mgrDec.sub_ar : mgrDec.sub;
                const t = mgrDec.tooltip;
                let timeStr = null;
                if (t?.timestamp) {
                  try { timeStr = format(new Date(t.timestamp), 'PPp'); }
                  catch { timeStr = t.timestamp; }
                }
                return (
                  <div className="space-y-2" data-testid="req-detail-manager-decision">
                    <h4 className="font-medium">{isRTL ? 'قرار المدير:' : 'Manager Decision:'}</h4>
                    <div className={cn('p-3 rounded text-sm', bg)}>
                      <p><strong>{label}</strong>{sub ? ` · ${sub}` : ''}</p>
                      {t?.actor_name && (
                        <p className="text-xs opacity-80 mt-1">
                          {isRTL ? 'بواسطة: ' : 'By: '}{t.actor_name}
                          {t.actor_email ? ` (${t.actor_email})` : ''}
                        </p>
                      )}
                      {timeStr && (
                        <p className="text-xs opacity-80">
                          {isRTL ? 'الوقت: ' : 'At: '}{timeStr}
                        </p>
                      )}
                      {t?.reason && <p className="mt-1">{t.reason}</p>}
                    </div>
                  </div>
                );
              })()}

              {/* HR Action — truthful per classifier (never defaults to Rejected) */}
              {(() => {
                const hrDec = classifyHrAction(viewDialog.request);
                if (!hrDec || hrDec.kind === 'none') return null;
                const bg =
                  hrDec.tone === 'green' ? 'bg-green-50 text-green-700'
                  : hrDec.tone === 'red' ? 'bg-red-50 text-red-700'
                  : hrDec.tone === 'amber' ? 'bg-amber-50 text-amber-700'
                  : 'bg-slate-50 text-slate-700';
                const label = isRTL ? hrDec.label_ar : hrDec.label;
                const sub = isRTL ? hrDec.sub_ar : hrDec.sub;
                const t = hrDec.tooltip;
                let timeStr = null;
                if (t?.timestamp) {
                  try { timeStr = format(new Date(t.timestamp), 'PPp'); }
                  catch { timeStr = t.timestamp; }
                }
                return (
                  <div className="space-y-2" data-testid="req-detail-hr-decision">
                    <h4 className="font-medium">{isRTL ? 'قرار الموارد البشرية:' : 'HR Decision:'}</h4>
                    <div className={cn('p-3 rounded text-sm', bg)}>
                      <p><strong>{label}</strong>{sub ? ` · ${sub}` : ''}</p>
                      {t?.actor_name && (
                        <p className="text-xs opacity-80 mt-1">
                          {isRTL ? 'بواسطة: ' : 'By: '}{t.actor_name}
                          {t.actor_email ? ` (${t.actor_email})` : ''}
                        </p>
                      )}
                      {timeStr && (
                        <p className="text-xs opacity-80">
                          {isRTL ? 'الوقت: ' : 'At: '}{timeStr}
                        </p>
                      )}
                      {t?.reason && <p className="mt-1">{t.reason}</p>}
                    </div>
                  </div>
                );
              })()}

              {/* Rejection Reason */}
              {viewDialog.request.rejection_reason && !viewDialog.request.manager_action && !viewDialog.request.hr_action && (
                <div className="space-y-2">
                  <h4 className="font-medium text-red-600">{isRTL ? 'سبب الرفض:' : 'Rejection Reason:'}</h4>
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded">{viewDialog.request.rejection_reason}</p>
                </div>
              )}

              {/* Attachments */}
              {viewDialog.request.attachments?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">{isRTL ? 'المرفقات:' : 'Attachments:'}</h4>
                  {viewDialog.request.attachments.map(att => (
                    <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                      <FileText className="h-4 w-4 text-slate-500" />
                      <span className="flex-1 text-sm">{att.file_name}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPreviewDialog({ open: true, attachment: att })}
                        title={isRTL ? 'معاينة' : 'View on screen'}
                        data-testid={`request-attachment-preview-${att.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadWithAuth(
                          `${API_URL}/requests/attachments/${att.id}/download`, att.file_name, token,
                        )}
                        title={isRTL ? 'تحميل' : 'Download'}
                        data-testid={`request-attachment-download-${att.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog({ open: false, request: null })}>
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 18.2.3.1: HR Override Dialog */}
      <Dialog open={overrideDialog.open} onOpenChange={(open) => !open && setOverrideDialog({ open: false, request: null, action: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-orange-600">
              {overrideDialog.action === 'approve'
                ? (isRTL ? 'تجاوز وموافقة على الطلب' : 'Override Approve Request')
                : (isRTL ? 'تجاوز ورفض الطلب' : 'Override Reject Request')}
            </DialogTitle>
            <DialogDescription>
              {overrideDialog.request?.employee_name?.trim()} - {overrideDialog.request?.request_type_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Override warning */}
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-orange-800">
                  <p className="font-medium">
                    {isRTL ? 'تحذير: إجراء التجاوز' : 'Warning: Override Action'}
                  </p>
                  <p className="mt-1">
                    {isRTL 
                      ? 'هذا الإجراء يتجاوز موافقة المدير وسيتم تسجيله في سجل التدقيق.' 
                      : 'This action bypasses manager approval and will be audited.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Override reason (required) */}
            <div className="space-y-2">
              <Label>
                {isRTL ? 'سبب التجاوز (مطلوب - 10 أحرف على الأقل)' : 'Override Reason (required - at least 10 characters)'}
              </Label>
              <Textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder={isRTL ? 'أدخل سبب التجاوز...' : 'Enter override reason...'}
                rows={3}
              />
              {overrideReason.length > 0 && overrideReason.length < 10 && (
                <p className="text-sm text-red-500">
                  {10 - overrideReason.length} {isRTL ? 'أحرف متبقية' : 'more characters needed'}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOverrideDialog({ open: false, request: null, action: null }); setOverrideReason(''); }}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleOverride}
              disabled={submitting || overrideReason.length < 10}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                overrideDialog.action === 'approve' 
                  ? (isRTL ? 'تجاوز وموافقة' : 'Override Approve') 
                  : (isRTL ? 'تجاوز ورفض' : 'Override Reject')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentPreview
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, attachment: null })}
        document={previewDialog.attachment}
        downloadUrl={previewDialog.attachment
          ? `${API_URL}/requests/attachments/${previewDialog.attachment.id}/download`
          : null}
        token={token}
        isRTL={isRTL}
        documentType="direct"
      />
    </div>
  );
};

export default RequestsAdminPage;
